import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  Heading,
  Text,
} from '@chakra-ui/react';
import './ForgotPasswordForm.scss';
import AuthSuccessMessage from '../AuthSuccessMessage/AuthSuccessMessage.tsx';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/AuthService.ts';

interface ForgotPasswordFormProps {
  onBackToLoginClick: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
                                                                 onBackToLoginClick,
                                                               }) => {
  const { t } = useTranslation();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [email, setEmail] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (email.trim() !== '') {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [email]);

  /**
   * Handles the "Reset password" button click.
   * Sets the state to show the AuthSuccessMessage component.
   * @returns {void}
   */
  const handleResetPasswordClick = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await AuthService.forgotPassword(email);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error in handleResetPasswordClick:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccessMessage) {
    return (
      <AuthSuccessMessage
        message={t('linkNewPassword')}
        onBackToLoginClick={onBackToLoginClick}
      />
    );
  }

  return (
    <Box textAlign="center" className="login-form">
      <Heading
        as="h1"
        size={{ base: 'md', md: 'lg' }}
        fontWeight={500}
        className="auth-title"
        mb={4}
      >
        {t('forgotPassword_Header')}
      </Heading>

      <Text
        textAlign="left"
        fontSize={{ base: '14px', md: '20px' }}
        mb={{ base: '40px', md: '57px' }}
      >
        {t('forgotPassword_Subline')}
      </Text>

      <FormControl id="email">
        <Input
          type="email"
          placeholder={t('username')}
          variant="outline-only-bottom"
          color="black"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>

      <Button
        colorScheme="alea"
        variant="solid"
        borderRadius={20}
        type="submit"
        mb={4}
        mt={{ base: '46px', md: '76px' }}
        width={{ base: '100%', md: '300px' }}
        height={{ base: '45px', md: '50px' }}
        fontSize={{ base: '14px', md: '18px' }}
        fontWeight={400}
        isDisabled={isButtonDisabled}
        isLoading={isSubmitting}
        onClick={handleResetPasswordClick}
      >
        {t('resetPassword_Button')}
      </Button>
    </Box>
  );
};

export default ForgotPasswordForm;
